export const SUBMIT_FORBIDDEN_ERROR = "You can only submit or edit products for your own organization.";
export const SUBMIT_GENERIC_ERROR = "We couldn't submit your product right now. Please try again. If this continues, contact support.";
export const SUBMIT_DUPLICATE_NAME_ERROR = "A product with this name already exists within your organization.";

export async function runSubmitProductFlow({
  workingProductId,
  product,
  hyperlink,
  loadedHyperlink,
  validateSubmitPayload,
  mapFieldFailures,
  buildProductSavePayload,
  buildHyperlinkPayloadForApi,
  hasHyperlinkChanges,
  updateHyperlink,
  createHyperlinkForProduct,
  updatePublishedProduct,
  submitProduct,
  vendorId,
  // Owner payload for create: `{ vendorId }` or `{ agencyId }`. Falls back to the legacy
  // `vendorId` arg when not provided so existing callers keep working.
  ownerPayload,
}) {
  const validationResult = validateSubmitPayload(product, hyperlink);
  if (!validationResult.isValid) {
    return {
      ok: false,
      type: "validation_errors",
      errors: mapFieldFailures(validationResult.failures),
    };
  }

  const productToSave = { ...buildProductSavePayload(product), isPublished: true, isActive: true };
  const hyperlinkPayload = buildHyperlinkPayloadForApi(hyperlink);
  let resolvedProductId = workingProductId;
  let shouldSetWorkingProductId = false;
  let nextLoadedHyperlink = null;
  let hyperlinkUploadFailed = false;
  let productUpdateFailed = false;

  try {
    if (workingProductId) {
      // Save the product (including productPoc) before touching the hyperlink so a
      // hyperlink failure below can never prevent the POC fields from being persisted.
      const updateProductResult = await updatePublishedProduct(
        workingProductId,
        productToSave
      );
      if (!updateProductResult.ok) {
        if (updateProductResult.reason === "forbidden") {
          return { ok: false, type: "inline_error", field: "form.general", message: SUBMIT_FORBIDDEN_ERROR };
        }
        productUpdateFailed = true;
      }

      try {
        if (hyperlink.id) {
          if (hasHyperlinkChanges(hyperlink, loadedHyperlink)) {
            const updateHyperlinkResponse = await updateHyperlink(hyperlink.id, { ...hyperlinkPayload, productId: workingProductId });
            if (updateHyperlinkResponse.status > 299) {
              hyperlinkUploadFailed = true;
            } else {
              nextLoadedHyperlink = { ...hyperlink };
            }
          }
        } else {
          const createHyperlinkResult = await createHyperlinkForProduct(workingProductId, hyperlinkPayload);
          if (!createHyperlinkResult.ok) {
            hyperlinkUploadFailed = true;
          }
        }
      } catch {
        // A thrown hyperlink error (e.g. network error or 5xx) must not be treated as a
        // fatal submit failure now that the product/POC save above has already completed.
        hyperlinkUploadFailed = true;
      }
    } else {
      const productResponse = await submitProduct({ ...productToSave, isPublished: false, ...(ownerPayload ?? { vendorId }) });
      if (productResponse.status >= 300) {
        if (productResponse?.status === 409) {
          return {
            ok: false,
            type: "inline_error",
            field: "product.name",
            message: SUBMIT_DUPLICATE_NAME_ERROR,
          };
        }
        return {
          ok: false,
          type: "inline_error",
          field: "product.name",
        message: SUBMIT_GENERIC_ERROR,
        };
      }
      resolvedProductId = productResponse.data.id;
      shouldSetWorkingProductId = true;

      try {
        const createHyperlinkResult = await createHyperlinkForProduct(resolvedProductId, hyperlinkPayload);
        if (!createHyperlinkResult.ok) {
          hyperlinkUploadFailed = true;
        }
      } catch {
        hyperlinkUploadFailed = true;
      }

      const updateProductResult = await updatePublishedProduct(
        resolvedProductId,
        productToSave
      );
      if (!updateProductResult.ok) {
        if (updateProductResult.reason === "forbidden") {
          return { ok: false, type: "inline_error", field: "form.general", message: SUBMIT_FORBIDDEN_ERROR };
        }
        productUpdateFailed = true;
      }
    }

    return {
      ok: true,
      resolvedProductId,
      shouldSetWorkingProductId,
      nextLoadedHyperlink,
      hyperlinkUploadFailed,
      productUpdateFailed,
    };
  } catch (e) {
    if (e?.response?.status === 409) {
      return { ok: false, type: "inline_error", field: "product.name", message: SUBMIT_DUPLICATE_NAME_ERROR };
    }
    if (e?.response?.status === 403) {
      return { ok: false, type: "inline_error", field: "form.general", message: SUBMIT_FORBIDDEN_ERROR };
    }
    return { ok: false, type: "inline_error", field: "form.general", message: SUBMIT_GENERIC_ERROR };
  }
}
